import { useQuery } from '@tanstack/react-query';
import { ROUTES, type Place, type Shipment } from '@serb/shared';
import { api, errorText } from '../../api/client';
import { KEYS } from '../../api/keys';
import { useCustomers } from '../customers/useCustomers';

/** The shipments, read from the Serb Mind vault by the server */
export function useShipments() {
  const q = useQuery({ queryKey: KEYS.shipments, queryFn: () => api.get<Shipment[]>(ROUTES.api.shipments) });
  return {
    items: q.data ?? [],
    loading: q.isPending,
    error: q.isError ? errorText(q.error, 'Shipments could not be loaded.') : '',
  };
}

/** The SRB code a new shipment should get: one more than the highest on the tracker. Empty until it is known. */
export function useNextSrb() {
  const q = useQuery({
    queryKey: KEYS.nextSrb,
    queryFn: () => api.get<{ srb: string }>(ROUTES.api.nextSrb),
    staleTime: 0,
    gcTime: 0,
  });
  return q.data?.srb ?? '';
}

export const placeName = (p: Place) => (p.code ? `${p.city} (${p.code})` : p.city);
const unique = (values: string[]) => [...new Set(values.filter((v) => v && v !== '–'))].sort();

/** The pick-lists on the New Shipment and New Quotation forms: customers, places and cargo types we already know, and our shipments */
export function useFormLists() {
  const { items: shipments } = useShipments();
  const customers = useCustomers().items;
  return {
    shipments,
    customerNames: unique([...customers.map((c) => c.name), ...shipments.map((s) => s.customer)]),
    places: unique(shipments.flatMap((s) => [placeName(s.from), placeName(s.to)])),
    cargoTypes: unique(shipments.map((s) => s.cargo)),
  };
}
